import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowDown, ArrowUp, Plus, Edit2, Trash2, Save, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { getVenueById } from '../services/venueService';
import {
  getVenueCourts,
  createCourt,
  updateCourt,
  updateCourtOrder,
  deleteCourt,
} from '../services/courtService';

export default function CourtManagement() {
  const { id: venueId } = useParams();
  const navigate = useNavigate();

  const [venue, setVenue] = useState(null);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newCourt, setNewCourt] = useState({ name: '', courtType: 'Outdoor' });
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [venueData, courtsData] = await Promise.all([
        getVenueById(venueId),
        getVenueCourts(venueId),
      ]);
      setVenue(venueData.venue || venueData);
      setCourts(Array.isArray(courtsData) ? courtsData : Array.isArray(courtsData.courts) ? courtsData.courts : []);
    } catch (err) {
      toast.error('Failed to load venue data');
      navigate('/venues');
    } finally {
      setLoading(false);
    }
  }, [venueId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleAddCourt(e) {
    e.preventDefault();
    if (!newCourt.name.trim()) {
      toast.error('Court name is required');
      return;
    }
    setSaving(true);
    try {
      const data = await createCourt(venueId, newCourt);
      setCourts((prev) => [...prev, data.court || data]);
      setShowAddForm(false);
      setNewCourt({ name: '', courtType: 'Outdoor' });
      toast.success('Court added');
    } catch (err) {
      toast.error('Failed to add court');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateCourt(courtId, updates) {
    try {
      const data = await updateCourt(courtId, updates);
      setCourts((prev) =>
        prev.map((c) => ((c._id || c.id) === courtId ? { ...c, ...(data.court || data) } : c))
      );
      setEditingCourt(null);
      toast.success('Court updated');
    } catch (err) {
      toast.error('Failed to update court');
    }
  }

  async function handleToggleStatus(court) {
    const courtId = court._id || court.id;
    const newActive = !court.isActive;
    try {
      await updateCourt(courtId, { isActive: newActive });
      setCourts((prev) =>
        prev.map((c) => ((c._id || c.id) === courtId ? { ...c, isActive: newActive } : c))
      );
      toast.success(`Court ${newActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  }

  async function handleDeleteCourt(courtId) {
    try {
      const result = await deleteCourt(courtId);
      if (result?.isDeleted) {
        setCourts((prev) => prev.filter((c) => (c._id || c.id) !== courtId));
      } else {
        setCourts((prev) => prev.map((court) =>
          (court._id || court.id) === courtId ? { ...court, isActive: false } : court
        ));
      }
      setDeleteConfirm(null);
      toast.success(result?.message || 'Court deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete court');
    }
  }

  async function moveCourt(courtId, direction) {
    const currentIndex = courts.findIndex((court) => (court._id || court.id) === courtId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= courts.length || reordering) return;

    const previousCourts = courts;
    const reorderedCourts = [...courts];
    [reorderedCourts[currentIndex], reorderedCourts[nextIndex]] = [reorderedCourts[nextIndex], reorderedCourts[currentIndex]];
    setCourts(reorderedCourts);
    setReordering(true);
    try {
      await updateCourtOrder(venueId, reorderedCourts.map((court) => court._id || court.id));
      toast.success('Court display order updated');
    } catch (err) {
      setCourts(previousCourts);
      toast.error(err.response?.data?.message || 'Failed to update court order');
    } finally {
      setReordering(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/venues')} className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1F2937]">Court Management</h1>
          <p className="text-sm text-[#6B7280]">
            {venue?.name || 'Venue'} — Manage courts
          </p>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Court
        </button>
      </div>

      {showAddForm && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-[#1F2937]">Add New Court</h3>
            <button onClick={() => setShowAddForm(false)} className="text-[#6B7280] hover:text-[#1F2937] transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleAddCourt} className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#6B7280]">Court Name</label>
              <input
                type="text"
                value={newCourt.name}
                onChange={(e) => setNewCourt((prev) => ({ ...prev, name: e.target.value }))}
                className="input-field mt-1"
                placeholder="e.g., Court 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Type</label>
              <select
                value={newCourt.courtType}
                onChange={(e) => setNewCourt((prev) => ({ ...prev, courtType: e.target.value }))}
                className="input-field mt-1"
              >
                <option value="Outdoor">Outdoor</option>
                <option value="Indoor">Indoor</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Adding...' : 'Add Court'}
            </button>
          </form>
        </div>
      )}

      {courts.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-[#6B7280]">
            <Plus className="h-full w-full" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-[#1F2937]">No courts yet</h3>
          <p className="mt-2 text-sm text-[#6B7280]">
            Add courts to start accepting bookings.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary mt-4"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Court
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-[#E2E8F0]">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Court Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {courts.map((court) => {
                const courtId = court._id || court.id;
                const isEditing = editingCourt === courtId;
                const index = courts.indexOf(court);

                return (
                  <tr key={courtId} className="hover:bg-[#F8FAFC]">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => moveCourt(courtId, -1)}
                          disabled={index === 0 || reordering}
                          title="Move court up"
                          className="rounded p-1 text-[#6B7280] hover:bg-[#E2E8F0] hover:text-[#264D59] disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => moveCourt(courtId, 1)}
                          disabled={index === courts.length - 1 || reordering}
                          title="Move court down"
                          className="rounded p-1 text-[#6B7280] hover:bg-[#E2E8F0] hover:text-[#264D59] disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="text"
                          defaultValue={court.name}
                          className="input-field"
                          autoFocus
                          onBlur={(e) => {
                            if (e.target.value.trim() && e.target.value !== court.name) {
                              handleUpdateCourt(courtId, { name: e.target.value.trim() });
                            } else {
                              setEditingCourt(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                            if (e.key === 'Escape') setEditingCourt(null);
                          }}
                        />
                      ) : (
                        <span
                          className="text-sm font-medium text-[#1F2937] cursor-pointer hover:text-[#264D59] transition-colors"
                          onClick={() => setEditingCourt(courtId)}
                        >
                          {court.name}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-[#6B7280] capitalize">{court.courtType || court.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(court)}
                        className="inline-flex items-center gap-1.5 text-sm"
                      >
                        {court.isActive ? (
                          <>
                            <ToggleRight className="h-5 w-5 text-[#56B7AC]" />
                            <span className="text-[#56B7AC] font-medium">Active</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="h-5 w-5 text-[#6B7280]" />
                            <span className="text-[#6B7280]">Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingCourt(courtId)}
                          className="text-[#6B7280] hover:text-[#264D59] transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {deleteConfirm === courtId ? (
                          <>
                            <button
                              onClick={() => handleDeleteCourt(courtId)}
                              className="text-[#D46C4E] hover:text-[#B3593E] text-sm font-medium"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-[#6B7280] hover:text-[#1F2937] text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(courtId)}
                            className="text-[#6B7280] hover:text-[#D46C4E] transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
